import { useManager } from 'dreamstate';
import React, { useEffect } from 'react';

import { BlockingLoadingWrapper } from '@vimeo/live-core/components/loading';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { InteractionSessionManager } from '@vimeo/live-core/store/interaction/session';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';
import { LiveError } from '@vimeo/live-core/utils/error';

import { createInteractionDomName } from '../../../libs/shared/utils/dom/identifying';
import { useInteractionToolsStatusObserverContext } from './embeding/InteractionToolsStatusObserver';
import {
  InteractionRoomLayout,
  InteractionLoginLayout,
  InteractionCollapsibleLoginLayout,
} from './layout';

/**
 * Int-x tools page that handles default flows of tools widget.
 */
export function InteractionToolsRouter({
  interactionSessionContext: {
    roomInfo: { value: roomInfo, isLoading: isRoomInfoLoading },
    roomUser: { value: roomUser, isLoading: isRoomUserLoading },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(InteractionSessionManager),
}) {
  const { initialState } = useLiveGlobals<IInteractionStoreInitialState>();
  const { setIsApplicationAvailableForModeration } = useInteractionToolsStatusObserverContext();

  useEffect(() => {
    if (!isRoomInfoLoading && !isRoomUserLoading) {
      setIsApplicationAvailableForModeration(
        Boolean(roomUser?.isCreator && !initialState?.interaction?.feature?.isManagementDisabled),
      );
    }
  }, [
    initialState?.interaction?.feature?.isManagementDisabled,
    isRoomInfoLoading,
    isRoomUserLoading,
    roomUser?.isCreator,
    setIsApplicationAvailableForModeration,
  ]);

  if (isRoomInfoLoading) {
    return (
      <BlockingLoadingWrapper
        id={createInteractionDomName('loader')}
        overlayColor={initialState?.interaction?.backgroundColor || 'background'}
        opacity={1}
        withLoader
      />
    );
  } else if (roomInfo) {
    if (roomUser) {
      return <InteractionRoomLayout />;
    } else if (
      initialState?.interaction?.auth?.isCollapsibleSignIn &&
      !initialState?.interaction?.isStandalone
    ) {
      return <InteractionCollapsibleLoginLayout />;
    } else {
      return <InteractionLoginLayout />;
    }
  } else {
    throw new LiveError('Failed to render interaction application without room info.');
  }
}
